import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, TagsIcon } from "lucide-react";
import { toast } from "sonner";

// Common booking slots — lunch & dinner. Partners tick the slots they accept.
const SLOT_OPTIONS = [
  { v: "12:00", label: "12:00 PM", group: "Lunch" },
  { v: "12:30", label: "12:30 PM", group: "Lunch" },
  { v: "13:00", label: "01:00 PM", group: "Lunch" },
  { v: "13:30", label: "01:30 PM", group: "Lunch" },
  { v: "14:00", label: "02:00 PM", group: "Lunch" },
  { v: "19:00", label: "07:00 PM", group: "Dinner" },
  { v: "19:30", label: "07:30 PM", group: "Dinner" },
  { v: "20:00", label: "08:00 PM", group: "Dinner" },
  { v: "20:30", label: "08:30 PM", group: "Dinner" },
  { v: "21:00", label: "09:00 PM", group: "Dinner" },
  { v: "21:30", label: "09:30 PM", group: "Dinner" },
  { v: "22:00", label: "10:00 PM", group: "Dinner" },
];

const DEFAULT_SLOTS = ["12:30", "13:30", "19:00", "20:00", "21:00"];

const blank = {
  name: "",
  list_price_per_head_inr: 1800,
  price_per_head_inr: 1500,
  inclusions: "Welcome drink, 3 starters, 4 mains, 2 desserts",
  min_pax: 5,
  max_pax: 40,
  vibe_tag: "Casual",
  available_slots: DEFAULT_SLOTS,
  pax_tiers: [],
  selection_rules: { starters: 3, mains: 4, desserts: 2, drinks_non_alc: 1 },
  is_banquet: false,
  menu_item_ids: [],
};

const PKG_CATEGORIES = [
  { v: "starters", label: "Starters" },
  { v: "mains", label: "Mains" },
  { v: "sides", label: "Sides & breads" },
  { v: "desserts", label: "Desserts" },
  { v: "drinks_non_alc", label: "Soft drinks" },
  { v: "drinks_alc", label: "Alcohol" },
];

export default function PartnerPackages() {
  const [list, setList] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const [{ data: pkgs }, { data: items }] = await Promise.all([
      api.get("/partner/packages"),
      api.get("/partner/menu-items"),
    ]);
    setList(pkgs);
    setMenuItems(items);
  };
  useEffect(() => { load(); }, []);

  const toggleDishInPkg = (mid) => {
    setForm((f) => ({
      ...f,
      menu_item_ids: f.menu_item_ids.includes(mid)
        ? f.menu_item_ids.filter((x) => x !== mid)
        : [...f.menu_item_ids, mid],
    }));
  };

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      list_price_per_head_inr: p.list_price_per_head_inr || p.price_per_head_inr,
      price_per_head_inr: p.price_per_head_inr,
      inclusions: (p.inclusions || []).join(", "),
      min_pax: p.min_pax,
      max_pax: p.max_pax,
      vibe_tag: p.vibe_tag,
      available_slots: p.available_slots && p.available_slots.length ? p.available_slots : DEFAULT_SLOTS,
      pax_tiers: (p.pax_tiers || []).map((t) => ({
        min_pax: t.min_pax,
        max_pax: t.max_pax,
        price_per_head_inr: t.price_per_head_inr,
        list_price_per_head_inr: t.list_price_per_head_inr || t.price_per_head_inr,
      })),
      selection_rules: p.selection_rules || {},
      is_banquet: !!p.is_banquet,
      menu_item_ids: p.menu_item_ids || [],
    });
    setOpen(true);
  };

  // ─── Pax-tier helpers ─────────────────────────────────────────
  const addTier = () => {
    setForm((f) => {
      const last = f.pax_tiers[f.pax_tiers.length - 1];
      const start = last ? Math.min(40, (last.max_pax || 5) + 1) : 5;
      const end = Math.min(40, start + 9);
      return {
        ...f,
        pax_tiers: [
          ...f.pax_tiers,
          {
            min_pax: start,
            max_pax: end,
            price_per_head_inr: parseInt(f.price_per_head_inr) || 1500,
            list_price_per_head_inr: parseInt(f.list_price_per_head_inr) || (parseInt(f.price_per_head_inr) || 1500),
          },
        ],
      };
    });
  };
  const updateTier = (idx, patch) => {
    setForm((f) => ({
      ...f,
      pax_tiers: f.pax_tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    }));
  };
  const removeTier = (idx) => {
    setForm((f) => ({ ...f, pax_tiers: f.pax_tiers.filter((_, i) => i !== idx) }));
  };

  const toggleSlot = (v) => {
    setForm((f) => {
      const has = f.available_slots.includes(v);
      const next = has ? f.available_slots.filter((s) => s !== v) : [...f.available_slots, v];
      return { ...f, available_slots: next.sort() };
    });
  };

  const save = async () => {
    const tabloPrice = parseInt(form.price_per_head_inr) || 0;
    const listPrice = parseInt(form.list_price_per_head_inr) || tabloPrice;
    if (tabloPrice <= 0) {
      toast.error("Tablo price must be greater than 0");
      return;
    }
    if (listPrice < tabloPrice) {
      toast.error("MRP / list price can't be less than the Tablo price");
      return;
    }
    if (!form.available_slots || form.available_slots.length === 0) {
      toast.error("Pick at least one time slot");
      return;
    }
    // Validate tiers — ranges within [5,40], min<=max, ascending and no overlap.
    const tiers = (form.pax_tiers || []).map((t) => ({
      min_pax: parseInt(t.min_pax) || 0,
      max_pax: parseInt(t.max_pax) || 0,
      price_per_head_inr: parseInt(t.price_per_head_inr) || 0,
      list_price_per_head_inr: parseInt(t.list_price_per_head_inr) || (parseInt(t.price_per_head_inr) || 0),
    })).filter((t) => t.min_pax > 0 && t.max_pax > 0);
    for (const t of tiers) {
      if (t.min_pax < 5 || t.max_pax > 40 || t.min_pax > t.max_pax || t.price_per_head_inr <= 0) {
        toast.error("Each tier needs min≤max, range within 5-40, and a positive price");
        return;
      }
      if (t.list_price_per_head_inr < t.price_per_head_inr) {
        toast.error("Tier MRP can't be less than tier Tablo price");
        return;
      }
    }
    // Check overlap
    const sorted = [...tiers].sort((a, b) => a.min_pax - b.min_pax);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].min_pax <= sorted[i - 1].max_pax) {
        toast.error(`Tiers ${sorted[i - 1].min_pax}-${sorted[i - 1].max_pax} and ${sorted[i].min_pax}-${sorted[i].max_pax} overlap`);
        return;
      }
    }
    const body = {
      name: form.name,
      price_per_head_inr: tabloPrice,
      list_price_per_head_inr: listPrice,
      inclusions: String(form.inclusions).split(",").map((s) => s.trim()).filter(Boolean),
      min_pax: parseInt(form.min_pax) || 5,
      max_pax: parseInt(form.max_pax) || 40,
      vibe_tag: form.vibe_tag,
      available_slots: form.available_slots,
      pax_tiers: tiers,
      selection_rules: form.selection_rules || {},
      is_banquet: !!form.is_banquet,
      menu_item_ids: form.menu_item_ids || [],
    };
    try {
      if (editing) {
        await api.patch(`/partner/packages/${editing.id}`, body);
        toast.success("Package updated");
      } else {
        await api.post("/partner/packages", body);
        toast.success("Package created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await api.delete(`/partner/packages/${id}`);
      toast.success("Package deleted");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/partner/packages/${p.id}`, { active_flag: !p.active_flag });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const discountPct = (() => {
    const tp = parseInt(form.price_per_head_inr) || 0;
    const lp = parseInt(form.list_price_per_head_inr) || 0;
    if (lp <= 0 || tp <= 0 || lp <= tp) return 0;
    return Math.round(((lp - tp) / lp) * 100);
  })();

  return (
    <div className="space-y-5 animate-fade-up">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">Packages</p>
          <h1 className="font-serif text-4xl">Group packages</h1>
          <p className="text-light-ink text-sm mt-2">Set an MRP and a Tablo price — guests see the savings. Your chef decides menu + portions by pax.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="tablo-btn-primary" data-testid="pkg-new-btn">
              <Plus className="w-4 h-4 mr-1" strokeWidth={2}/>New package
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-cream max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit" : "Create"} package</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Name</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Premium Dinner" className="bg-white border-tablo-border" data-testid="pkg-name"/>
              </div>

              {/* Pricing — MRP vs Tablo */}
              <div className="rounded-xl border border-tablo-border bg-white/60 p-3 space-y-3">
                <p className="text-xs uppercase tracking-wider text-light-ink flex items-center gap-1">
                  <TagsIcon className="w-3.5 h-3.5" strokeWidth={1.6}/> Pricing
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-light-ink mb-1 block">MRP / head (₹)</label>
                    <Input type="number" value={form.list_price_per_head_inr} onChange={(e) => setForm({ ...form, list_price_per_head_inr: e.target.value })} className="bg-white border-tablo-border" data-testid="pkg-list-price"/>
                    <p className="text-[10px] text-light-ink/70 mt-1">Walk-in / menu card price.</p>
                  </div>
                  <div>
                    <label className="text-[11px] text-light-ink mb-1 block">Tablo price / head (₹)</label>
                    <Input type="number" value={form.price_per_head_inr} onChange={(e) => setForm({ ...form, price_per_head_inr: e.target.value })} className="bg-white border-tablo-border" data-testid="pkg-price"/>
                    <p className="text-[10px] text-light-ink/70 mt-1">Pre-paid via Tablo group bookings.</p>
                  </div>
                </div>
                <div className="text-xs flex items-center justify-between bg-cream rounded-lg px-3 py-2" data-testid="pkg-discount-preview">
                  <span className="text-light-ink">Guest sees discount:</span>
                  {discountPct > 0 ? (
                    <span className="font-medium text-amber">{discountPct}% OFF · save ₹{(parseInt(form.list_price_per_head_inr) || 0) - (parseInt(form.price_per_head_inr) || 0)}/head</span>
                  ) : (
                    <span className="text-light-ink/70">No discount yet — bump the MRP above the Tablo price.</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Vibe tag</label>
                  <Input value={form.vibe_tag} onChange={(e) => setForm({ ...form, vibe_tag: e.target.value })} className="bg-white border-tablo-border"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Min pax</label>
                  <Input type="number" value={form.min_pax} onChange={(e) => setForm({ ...form, min_pax: e.target.value })} className="bg-white border-tablo-border"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Max pax</label>
                  <Input type="number" value={form.max_pax} onChange={(e) => setForm({ ...form, max_pax: e.target.value })} className="bg-white border-tablo-border"/>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Inclusions (comma-separated, summary text)</label>
                <Input value={form.inclusions} onChange={(e) => setForm({ ...form, inclusions: e.target.value })} className="bg-white border-tablo-border"/>
                <p className="text-[10px] text-light-ink/70 mt-1">High-level summary shown on cards. The structured menu picks come from "Selection rules" below.</p>
              </div>

              {/* Selection rules — how many dishes per category the guest can pick */}
              <div className="rounded-xl border border-tablo-border bg-white/60 p-3 space-y-2">
                <p className="text-xs uppercase tracking-wider text-light-ink flex items-center gap-1">Selection rules</p>
                <p className="text-[11px] text-light-ink/70">
                  Sets the menu the guest sees — e.g. <i>"pick 3 starters from the menu"</i>. Categories come from your menu items.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {PKG_CATEGORIES.map((c) => (
                    <div key={c.v} className="flex items-center gap-2 bg-cream rounded-lg px-2.5 py-1.5">
                      <span className="text-xs flex-1">{c.label}</span>
                      <Input
                        type="number"
                        min={0}
                        max={20}
                        value={form.selection_rules?.[c.v] ?? 0}
                        onChange={(e) => setForm({
                          ...form,
                          selection_rules: {
                            ...(form.selection_rules || {}),
                            [c.v]: parseInt(e.target.value) || 0,
                          },
                        })}
                        className="bg-white border-tablo-border h-8 w-16 text-sm text-right"
                        data-testid={`pkg-rule-${c.v}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Banquet toggle */}
              <div className="flex items-center justify-between bg-cream rounded-xl px-3 py-2.5">
                <div>
                  <p className="text-sm font-medium">Banquet package</p>
                  <p className="text-[11px] text-light-ink/70">Shown only when bookers pick 100+ guests / banquet mode.</p>
                </div>
                <Switch checked={!!form.is_banquet} onCheckedChange={(v) => setForm({ ...form, is_banquet: v })} data-testid="pkg-is-banquet"/>
              </div>

              {/* Dishes offered in this package */}
              <div className="rounded-xl border border-tablo-border bg-white/60 p-3 space-y-2" data-testid="pkg-dish-picker">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs uppercase tracking-wider text-light-ink">Dishes offered in this package</p>
                  <span className="text-[11px] text-light-ink">{form.menu_item_ids.length} picked</span>
                </div>
                <p className="text-[11px] text-light-ink/70">
                  Pick dishes from your menu that guests choose from inside this package. Honour the selection rules above (e.g. if "Starters: 3" guests pick 3 from your starter list here).
                </p>
                {menuItems.length === 0 ? (
                  <p className="text-[11px] text-light-ink italic">Add menu items first under the Menu tab.</p>
                ) : (
                  <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
                    {PKG_CATEGORIES.map((c) => {
                      const itemsInCat = menuItems.filter((m) => m.category === c.v && m.active_flag !== false);
                      if (itemsInCat.length === 0) return null;
                      return (
                        <div key={c.v}>
                          <p className="text-[10px] uppercase tracking-wider text-light-ink/70 mb-1">{c.label}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {itemsInCat.map((m) => {
                              const active = form.menu_item_ids.includes(m.id);
                              return (
                                <button
                                  key={m.id}
                                  type="button"
                                  onClick={() => toggleDishInPkg(m.id)}
                                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1 ${active ? "bg-ink text-cream border-ink" : "bg-white text-ink border-tablo-border hover:border-amber/60"}`}
                                  data-testid={`pkg-dish-${m.id}`}
                                >
                                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${m.veg_flag ? "bg-green-500" : "bg-rose-500"}`}/>
                                  {m.name} <span className="opacity-70">₹{m.price_inr}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Time slot picker — checkbox grid */}
              <div>
                <label className="text-xs uppercase tracking-wider text-light-ink mb-2 block">Available time slots</label>
                {["Lunch", "Dinner"].map((group) => (
                  <div key={group} className="mb-2">
                    <p className="text-[10px] uppercase tracking-wider text-light-ink/70 mb-1.5">{group}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {SLOT_OPTIONS.filter((s) => s.group === group).map((s) => {
                        const active = form.available_slots.includes(s.v);
                        return (
                          <button
                            key={s.v}
                            type="button"
                            onClick={() => toggleSlot(s.v)}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-ink text-cream border-ink" : "bg-white text-ink border-tablo-border hover:border-amber/60"}`}
                            data-testid={`pkg-slot-${s.v}`}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {form.available_slots.length === 0 && (
                  <p className="text-[11px] text-rose-600 mt-1">Pick at least one slot.</p>
                )}
              </div>

              {/* Pax-tier editor — restaurant defines per-head prices for different group sizes */}
              <div className="rounded-xl border border-tablo-border bg-white/60 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-wider text-light-ink">Pricing by group size (optional)</p>
                  <button
                    type="button"
                    onClick={addTier}
                    className="text-xs text-amber font-medium hover:underline inline-flex items-center gap-1"
                    data-testid="pkg-add-tier-btn"
                  >
                    <Plus className="w-3 h-3" strokeWidth={2}/> Add tier
                  </button>
                </div>
                <p className="text-[11px] text-light-ink/70">
                  Different per-head price for small vs large groups. Tiers must fit in 5-40 pax and not overlap. If no tiers are set, the package price above applies to every group size.
                </p>
                {form.pax_tiers.length === 0 ? (
                  <p className="text-[11px] text-light-ink italic">No tiers — flat price applies.</p>
                ) : (
                  <div className="space-y-2">
                    {form.pax_tiers.map((t, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-1.5 items-center" data-testid={`pkg-tier-row-${idx}`}>
                        <Input type="number" value={t.min_pax} onChange={(e) => updateTier(idx, { min_pax: e.target.value })} placeholder="Min pax" className="bg-white border-tablo-border h-9 text-sm" data-testid={`pkg-tier-min-${idx}`}/>
                        <Input type="number" value={t.max_pax} onChange={(e) => updateTier(idx, { max_pax: e.target.value })} placeholder="Max pax" className="bg-white border-tablo-border h-9 text-sm" data-testid={`pkg-tier-max-${idx}`}/>
                        <Input type="number" value={t.list_price_per_head_inr} onChange={(e) => updateTier(idx, { list_price_per_head_inr: e.target.value })} placeholder="MRP" className="bg-white border-tablo-border h-9 text-sm" data-testid={`pkg-tier-mrp-${idx}`}/>
                        <Input type="number" value={t.price_per_head_inr} onChange={(e) => updateTier(idx, { price_per_head_inr: e.target.value })} placeholder="Tablo" className="bg-white border-tablo-border h-9 text-sm" data-testid={`pkg-tier-price-${idx}`}/>
                        <button
                          type="button"
                          onClick={() => removeTier(idx)}
                          className="p-1.5 hover:bg-rose-50 text-rose-600 rounded"
                          data-testid={`pkg-tier-del-${idx}`}
                        ><Trash2 className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                      </div>
                    ))}
                    <p className="text-[10px] uppercase tracking-wider text-light-ink/70">Min · Max · MRP · Tablo</p>
                  </div>
                )}
              </div>

              <Button onClick={save} className="w-full tablo-btn-primary" data-testid="pkg-save-btn">{editing ? "Save changes" : "Create package"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </header>

      <div className="tablo-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cream">
            <tr className="text-xs uppercase tracking-wider text-light-ink">
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">MRP → Tablo</th>
              <th className="text-left p-3">Discount</th>
              <th className="text-left p-3">Pax</th>
              <th className="text-left p-3">Vibe</th>
              <th className="text-left p-3">Active</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (<tr><td colSpan={7} className="p-6 text-center text-light-ink">No packages yet. Tap "New package" above.</td></tr>)}
            {list.map((p) => {
              const lp = p.list_price_per_head_inr || p.price_per_head_inr;
              const pct = lp > p.price_per_head_inr ? Math.round(((lp - p.price_per_head_inr) / lp) * 100) : 0;
              return (
                <tr key={p.id} className="border-t border-tablo-border hover:bg-off-white" data-testid={`pkg-row-${p.id}`}>
                  <td className="p-3"><div className="font-medium">{p.name}</div><div className="text-xs text-light-ink">{(p.inclusions || []).join(" · ")}</div></td>
                  <td className="p-3">
                    {lp > p.price_per_head_inr && <span className="line-through text-xs text-light-ink/70 mr-1">₹{lp}</span>}
                    <span className="font-medium">₹{p.price_per_head_inr}</span>
                  </td>
                  <td className="p-3">{pct > 0 ? <span className="tablo-pill bg-amber-soft/40 text-ink">{pct}% OFF</span> : <span className="text-xs text-light-ink/60">—</span>}</td>
                  <td className="p-3">
                    {p.min_pax}–{p.max_pax}
                    {p.pax_tiers && p.pax_tiers.length > 0 && (
                      <span className="ml-1.5 tablo-pill bg-cream text-amber text-[10px]" data-testid={`pkg-tier-pill-${p.id}`}>{p.pax_tiers.length} tiers</span>
                    )}
                  </td>
                  <td className="p-3 text-xs">{p.vibe_tag}</td>
                  <td className="p-3"><Switch checked={!!p.active_flag} onCheckedChange={() => toggleActive(p)} data-testid={`pkg-active-${p.id}`}/></td>
                  <td className="p-3 text-right">
                    <button onClick={() => openEdit(p)} className="p-1.5 hover:bg-cream rounded" data-testid={`pkg-edit-${p.id}`}><Pencil className="w-4 h-4" strokeWidth={1.6}/></button>
                    <button onClick={() => remove(p.id)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded ml-1" data-testid={`pkg-del-${p.id}`}><Trash2 className="w-4 h-4" strokeWidth={1.6}/></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
